import React, { FC, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import useRootSelector from 'common/hooks/useRootSelector';
import InvoiceTotals from '../InvoiceTotals/InvoiceTotals';
import { ButtonWithCircularProgress } from 'common/components';
import { CustomTheme } from 'common/ui/interfaces';
import * as selectors from 'common/state/invoice/selectors';
import * as actions from 'common/state/invoice/actions';
import { selectPosKioskRegisterNOSAmount } from 'modules/pos-kiosk/state/register/selectors';
import { useSearchInvoiceProductsState } from 'common/createContext/searchInvoiceProductsContext';
import { useAppDispatch } from 'store/hooks';
import messages from 'common/messages/messages';
import { CartUnitTypes, IInvoiceDetailsImt } from 'common/interfaces/invoices';
import { PeakModules } from 'common/constants/peakModules';
import { Message } from 'common/interfaces/common';
import { priceRounding } from 'common/utils';
import { AS_PAID_INVOICE_PAYMENT_SPLIT_STATUS } from '../constants';
import AddDiscountModal from '../InvoiceModals/AddDiscountModal/AddDiscountModal';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import AddTipsModal from '../InvoiceModals/AddTipsModal/AddTipsModal';

const useStyles = makeStyles((theme: CustomTheme) => ({
  footerDivider: {
    borderTop: `1px solid ${theme.palette.borderColor?.light}`,
  },
  footer: {
    flex: '1 1 auto',
    paddingTop: theme.spacing(3),
    margin: theme.spacing(0, 2, 2, 2),
  },
  actionButton: {
    width: '100%',
    padding: theme.spacing(1),
  },
}));

interface IInvoiceFooter {
  invoice: IInvoiceDetailsImt;
  clubId: string;
  handleCancelInvoice: () => void;
  handleVoidInvoice: () => void;
  handleAddMember: () => void;
  handleInvoiceActionClick: () => void;
  module: PeakModules;
  isPaymentStep: boolean;
  memberId: number;
}

export const InvoiceFooter: FC<IInvoiceFooter> = ({
  invoice,
  handleInvoiceActionClick,
  handleAddMember,
  handleCancelInvoice,
  handleVoidInvoice,
  module,
  memberId,
  clubId,
  isPaymentStep,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { GIVE_DISCOUNT, VOID_INVOICE } = usePermissionContext();

  const [isAddDiscountModalOpen, setIsAddDiscountModalOpen] = useState(false);
  const [isAddTipsModalOpen, setIsAddTipsModalOpen] = useState(false);

  const loadingCheckOut: boolean = useRootSelector(selectors.selectUpdatedInvoiceLoading);
  const registerNosValue = useRootSelector(selectPosKioskRegisterNOSAmount);

  const {
    toggleShowAddInventories,
    showSearchProducts: { isShowAddInventories, isShowAddServicePackages, isShowAddGiftCards },
  } = useSearchInvoiceProductsState();

  const convertedInvoice = useMemo(() => invoice.toJS(), [invoice]);

  const totalInvoicePayments =
    convertedInvoice?.invoicePaymentSplits
      ?.filter(p => (p.status ? AS_PAID_INVOICE_PAYMENT_SPLIT_STATUS.includes(p.status) : null))
      .reduce((acc, value) => {
        return acc + Number(value.paymentAmount);
      }, 0) || 0;

  const leftToPay = priceRounding(convertedInvoice.totalAmount - totalInvoicePayments);

  const isSearchProductsActive =
    isShowAddInventories || isShowAddServicePackages || isShowAddGiftCards;

  const isEmptyCart = !convertedInvoice.invoiceUnits?.length;

  const isAmountExceedNOS = convertedInvoice?.totalAmount > registerNosValue;
  const hasNosError = isAmountExceedNOS && !convertedInvoice?.customer;
  const shouldVisibleAddPaymentBtnCondition = !isAmountExceedNOS;
  const atLeastOneIsNotEmpty = convertedInvoice.invoiceUnits?.length;
  const shouldVisibleProceedBtn = atLeastOneIsNotEmpty && !convertedInvoice?.totalAmount;

  const hasServiceError =
    !convertedInvoice?.customer && atLeastOneIsNotEmpty && !shouldVisibleAddPaymentBtnCondition;
  const hasSalesPersonError = !convertedInvoice?.salesperson;
  const hasPaymentMethods = !!convertedInvoice?.invoicePaymentSplits?.length;

  const isAddPaymentActionDisabled = isEmptyCart || hasNosError || hasSalesPersonError;

  const handleBackToCartClick = () => {
    toggleShowAddInventories(false);
  };

  const handleProceed = () => {
    dispatch(
      actions.proceedFreeInvoice(convertedInvoice.id, module, clubId, memberId, isPaymentStep),
    );
  };

  const handleAddDiscount = (values: { discount: number }) => {
    dispatch(
      actions.addInvoiceDiscount(convertedInvoice.id, values.discount, module, isPaymentStep),
    );
  };

  const onOpenAddDiscountModal = () => {
    setIsAddDiscountModalOpen(true);
  };

  const getSubmitBtnTitle = (): Message => {
    if (hasServiceError) {
      return messages.newMemberCommonTitle;
    }
    if (shouldVisibleProceedBtn) {
      return messages.proceedBtn;
    }
    return messages.addPayMethod;
  };

  const getSubmitBtnHandler = (): (() => void) => {
    if (hasServiceError) {
      return handleAddMember;
    }
    if (shouldVisibleProceedBtn) {
      return handleProceed;
    }
    return handleInvoiceActionClick;
  };

  const getCancelBtn = (): JSX.Element | null => {
    let onClick: (() => void) | undefined;
    let text: string;

    if (hasPaymentMethods) {
      if (!VOID_INVOICE) {
        return null;
      }

      onClick = handleVoidInvoice;
      text = intl.formatMessage(messages.voidInvoice);
    } else {
      onClick = handleCancelInvoice;
      text = intl.formatMessage(messages.cancelInvoice);
    }

    return (
      <Button className={classes.actionButton} color="primary" type="button" onClick={onClick}>
        {text}
      </Button>
    );
  };

  useEffect(() => {
    setIsAddDiscountModalOpen(false);
  }, [invoice]);

  return (
    <Box
      className={cx(classes.footer, {
        [classes.footerDivider]: !isEmptyCart && !isSearchProductsActive,
      })}
    >
      {!isEmptyCart && !isSearchProductsActive ? (
        <InvoiceTotals
          payments={convertedInvoice?.invoicePaymentSplits}
          total={convertedInvoice.totalAmount}
          subtotal={convertedInvoice.subtotalAmount}
          tax={convertedInvoice.taxAmount}
          discount={convertedInvoice?.discount}
          discountAmount={convertedInvoice?.discountAmount}
          leftToPay={leftToPay}
          onAddDiscount={GIVE_DISCOUNT ? onOpenAddDiscountModal : undefined}
          onAddTips={() => setIsAddTipsModalOpen(true)}
          disableAddDiscount={hasSalesPersonError}
          tips={convertedInvoice.tipsAmount}
        />
      ) : null}
      <Box pt={2}>
        <Grid container spacing={1}>
          {isSearchProductsActive ? (
            <Grid item xs={12}>
              <Button
                className={classes.actionButton}
                color="primary"
                type="button"
                variant="contained"
                onClick={handleBackToCartClick}
              >
                <FormattedMessage {...messages.backToCartBtn} />
              </Button>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                {getCancelBtn()}
              </Grid>

              <Grid item xs={12} md={6}>
                <ButtonWithCircularProgress
                  className={classes.actionButton}
                  type="button"
                  disabled={hasServiceError && !isEmptyCart ? false : isAddPaymentActionDisabled}
                  isSubmitting={loadingCheckOut}
                  onClick={getSubmitBtnHandler()}
                >
                  <FormattedMessage {...getSubmitBtnTitle()} />
                </ButtonWithCircularProgress>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      {isAddDiscountModalOpen ? (
        <AddDiscountModal
          salespersonDiscount={invoice?.getIn(['salesperson', 'posDiscountPercentage'])}
          onClose={() => setIsAddDiscountModalOpen(false)}
          onSubmit={handleAddDiscount}
          discountValue={invoice?.get('discount')}
        />
      ) : null}

      {isAddTipsModalOpen ? (
        <AddTipsModal
          onClose={() => setIsAddTipsModalOpen(false)}
          onSubmit={tipsAmount => {
            if (convertedInvoice.tipsAmount !== tipsAmount) {
              if (tipsAmount === 0) {
                const tipsInvoiceUnit = convertedInvoice.invoiceUnits.find(item => {
                  return item.type === CartUnitTypes.TIPS;
                });

                if (tipsInvoiceUnit) {
                  dispatch(
                    actions.deleteInvoiceUnit(
                      convertedInvoice.id,
                      tipsInvoiceUnit.id,
                      module,
                      false,
                    ),
                  );
                }
              } else {
                dispatch(
                  actions.addInvoiceUnit(
                    convertedInvoice.id,
                    { tipsAmount },
                    CartUnitTypes.TIPS,
                    module,
                    false,
                  ),
                );
              }
            }

            setIsAddTipsModalOpen(false);
          }}
          totalAmount={convertedInvoice.totalAmount || 0}
          tips={convertedInvoice.tipsAmount || 0}
        />
      ) : null}
    </Box>
  );
};
